// src/services/templateService.js
// Template generation — mapping is stored in the "TemplateMappings" SharePoint list,
// NOT hardcoded. Admins manage it through the Template Library page.
//
// TemplateMappings list schema:
//   Title          — contract type label (e.g. "NDA", "Service")
//   TemplateFileName — file name inside the Templates folder (e.g. "NDA_Template.docx")
//   IsActive       — "Yes" / "No"  (only "Yes" entries are used for draft generation)

import { enableTrackChangesInDocx } from "./trackChangesService";

const TEMPLATES_FOLDER = "Templates";

// ─── Placeholder catalogue ─────────────────────────────────────────────────────
// Single source of truth used by both the token injector and the UI guide.
export const TEMPLATE_PLACEHOLDERS = [
  // Contract Details
  { token: "{{ContractTitle}}",        label: "Contract Title",               group: "Contract Details", source: "Contract Title field"                },
  { token: "{{RefNo}}",                label: "Reference Number",             group: "Contract Details", source: "Auto-generated on submission"        },
  { token: "{{ContractType}}",         label: "Contract Template",           group: "Contract Details", source: "Contract Template dropdown"           },
  { token: "{{RequestDate}}",          label: "Date of Request",              group: "Contract Details", source: "Date of Request field"               },
  { token: "{{EffectiveDate}}",        label: "Effective Date",               group: "Contract Details", source: "Effective Date field"                },
  { token: "{{ExpiryDate}}",           label: "Expiry Date",                  group: "Contract Details", source: "Expiry Date field"                   },
  { token: "{{Duration}}",             label: "Duration",                     group: "Contract Details", source: "Duration field (e.g. 24 months)"     },
  { token: "{{Renewable}}",            label: "Renewable",                    group: "Contract Details", source: "Renewable radio (Yes / No)"          },
  { token: "{{Year}}",                 label: "Contract Year",                group: "Contract Details", source: "Derived from submission date"        },
  { token: "{{Month}}",                label: "Contract Month",               group: "Contract Details", source: "Derived from effective date"         },
  // Parties
  { token: "{{Entity}}",               label: "Contracting Entity",           group: "Parties",          source: "Contracting Entity dropdown"         },
  { token: "{{Department}}",           label: "User Department",              group: "Parties",          source: "User Department dropdown"            },
  { token: "{{CategoryManager}}",      label: "Category Manager",             group: "Parties",          source: "Category Manager field"              },
  { token: "{{ContractOwner}}",        label: "Contract Owner",               group: "Parties",          source: "Contract Owner field"                },
  { token: "{{SupplierName}}",         label: "Supplier Name",                group: "Parties",          source: "Supplier picker"                     },
  { token: "{{RequestedBy}}",          label: "Requested By (email)",         group: "Parties",          source: "Logged-in user email"                },
  // Financial
  { token: "{{CeilingMonthly}}",       label: "Ceiling Value (Monthly)",      group: "Financial",        source: "Ceiling Value (Monthly) field"       },
  { token: "{{CeilingAnnual}}",        label: "Ceiling Value (Annual)",       group: "Financial",        source: "Ceiling Value (Annual) field"        },
  { token: "{{DeviationFromPayment}}", label: "Deviation from Payment Terms", group: "Financial",        source: "Deviation from Payment Terms radio"  },
  // Addendum
  { token: "{{ParentContractRef}}",    label: "Parent Contract Ref",          group: "Addendum",         source: "Parent Contract (Addendum only)"     },
  // Other
  { token: "{{Comments}}",             label: "Additional Comments",          group: "Other",            source: "Additional Information / Comments"   },
  { token: "{{TODAY}}",                label: "Today's Date",                 group: "Other",            source: "Auto-filled with current date"       },
];

// ─── Token map builder ─────────────────────────────────────────────────────────
const buildTokenMap = (data) => {
  const fmt = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const monthName = data.month ? (months[(parseInt(data.month) || 1) - 1] || "") : "";

  return {
    "{{ContractTitle}}":        data.contractTitle        || "",
    "{{RefNo}}":                data.refNo                || "",
    "{{ContractType}}":         data.contractType         || "",
    "{{RequestDate}}":          fmt(data.requestDate),
    "{{EffectiveDate}}":        fmt(data.effectiveDate),
    "{{ExpiryDate}}":           fmt(data.expiryDate),
    "{{Duration}}":             data.duration             || "",
    "{{Renewable}}":            data.isRenewal            || "No",
    "{{IsRenewal}}":            data.isRenewal            || "No",   // backward-compat alias
    "{{Year}}":                 data.year                 || String(new Date().getFullYear()),
    "{{Month}}":                monthName,
    "{{Entity}}":               data.entity               || "",
    "{{Department}}":           data.department           || "",
    "{{CategoryManager}}":      data.categoryManager      || "",
    "{{ContractOwner}}":        data.contractOwner        || "",
    "{{SupplierName}}":         data.vendorName           || "",
    "{{VendorName}}":           data.vendorName           || "",     // backward-compat alias
    "{{RequestedBy}}":          data.requestedBy          || "",
    "{{CeilingMonthly}}":       data.ceilingValueMonthly  || "",
    "{{CeilingAnnual}}":        data.ceilingValueAnnual   || "",
    "{{DeviationFromPayment}}": data.deviationFromPayment || "",
    "{{ParentContractRef}}":    data.parentContractRef    || "",
    "{{Comments}}":             data.additionalComments   || "",
    "{{TODAY}}":                new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
  };
};

// ─── XML helpers ───────────────────────────────────────────────────────────────
const xmlEsc = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const injectTokens = (xml, tokenMap) =>
  xml.replace(/<w:t([^>]*)>([\s\S]*?)<\/w:t>/g, (match, attrs, text) => {
    let updated = text;
    Object.entries(tokenMap).forEach(([token, value]) => {
      updated = updated.replace(
        new RegExp(token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
        xmlEsc(value)
      );
    });
    return `<w:t${attrs}>${updated}</w:t>`;
  });

const refToFileName = (refNo) => refNo.replace(/[/\\]/g, "-") + ".docx";

// ─── Dynamic mapping lookup ────────────────────────────────────────────────────
/**
 * Fetch the template file name for a given contract type from the
 * "TemplateMappings" SharePoint list. Returns null if no active mapping exists.
 */
export const getTemplateName = async (service, contractType) => {
  if (!contractType) return null;
  try {
    const data = await service.getListItems(
      "TemplateMappings",
      `fields/Title eq '${contractType}' and fields/IsActive eq 'Yes'`
    );
    const match = data?.value?.[0];
    return match?.fields?.TemplateFileName || null;
  } catch {
    return null;
  }
};

/**
 * Fetch all mappings from the list (for the management UI).
 */
export const getAllMappings = async (service) => {
  const data = await service.getListItems("TemplateMappings");
  return (data?.value || []).map(i => ({ id: i.id, ...i.fields }));
};

// ─── Shared low-level generator ────────────────────────────────────────────────
// Downloads a specific template file, injects tokens, and uploads the result.
// Used by both the auto-mapped contract draft generator and the CRAF generator
// (which lets the admin pick the template file directly instead of relying on
// the ContractType → TemplateMappings lookup).
const generateDraftFromTemplateFile = async ({
  service,
  contractData,
  folderPath,
  templateFileName,
  outputFileName,
}) => {
  // 1. Get the ContractDocuments drive
  const drives = await service.getDrives();
  const docsDrive = drives?.value?.find((d) => d.name === "ContractDocuments");
  if (!docsDrive) throw new Error("ContractDocuments drive not found");

  // 2. Download template from Templates folder
  const templatePath = TEMPLATES_FOLDER + "/" + templateFileName;
  const templateBuffer = await service.downloadFile(docsDrive.id, templatePath);

  // 3. Unzip, inject tokens, re-zip
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(templateBuffer);
  const tokenMap = buildTokenMap(contractData);

  const TEXT_FILES = /^word\/(document|header\d*|footer\d*)\.xml$/;
  for (const fname of Object.keys(zip.files).filter(n => TEXT_FILES.test(n))) {
    const file = zip.file(fname);
    if (!file) continue;
    zip.file(fname, injectTokens(await file.async("string"), tokenMap));
  }

  let outputBlob = await zip.generateAsync({
    type: "arraybuffer",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });

  console.log("[LexCore] Draft generated, size:", outputBlob.byteLength, "bytes");

  // 3.5. Enable Track Changes in the generated document
  try {
    console.log("[LexCore] Attempting to enable Track Changes in document...");
    const trackChangesBuffer = await enableTrackChangesInDocx(outputBlob);
    outputBlob = trackChangesBuffer;
    console.log("[LexCore] Track Changes enabled successfully. Document size:", outputBlob.byteLength, "bytes");
  } catch (e) {
    console.error("[LexCore] Track Changes modification encountered error:", e);
    console.warn("[LexCore] Proceeding with document generation (Track Changes may not be enabled)");
  }

  // 4. Upload draft into the contract folder
  const draftFileName = outputFileName || refToFileName(contractData.refNo);
  const uploaded = await service.uploadFile(docsDrive.id, folderPath, draftFileName, outputBlob);

  return {
    fileName: draftFileName,
    webUrl: uploaded?.webUrl,
    fileId: uploaded?.id,
    templateUsed: templateFileName,
  };
};

// ─── Main draft generator ──────────────────────────────────────────────────────
export const generateContractDraft = async ({
  service,
  contractData,
  folderPath,
  contractId,
  uploadedBy,
  outputFileName,   // optional — overrides the default refNo-based file name
}) => {
  // Look up the mapped template file name from SharePoint
  const templateFileName = await getTemplateName(service, contractData.contractType);
  if (!templateFileName) {
    console.warn("[LexCore] No active template mapping for:", contractData.contractType);
    return null;
  }

  return generateDraftFromTemplateFile({ service, contractData, folderPath, templateFileName, outputFileName });
};

// ─── CRAF draft generator ──────────────────────────────────────────────────────
// Unlike generateContractDraft, the template is NOT looked up by contract type —
// the admin picks it explicitly from the Templates folder (see listTemplateFiles).
export const generateCrafDraft = async ({
  service,
  contractData,
  folderPath,
  templateFileName,
  outputFileName,
}) => {
  if (!templateFileName) throw new Error("No CRAF template selected");
  return generateDraftFromTemplateFile({ service, contractData, folderPath, templateFileName, outputFileName });
};

// ─── Template file listing (for the CRAF template picker) ─────────────────────
export const listTemplateFiles = async (service) => {
  const drives = await service.getDrives();
  const docsDrive = drives?.value?.find((d) => d.name === "ContractDocuments");
  if (!docsDrive) return [];
  const contents = await service.getFolderContents(docsDrive.id, TEMPLATES_FOLDER);
  return (contents?.value || []).filter((i) => !i.folder && i.name.toLowerCase().endsWith(".docx"));
};
